import { useState } from "react";
import { Link, createFileRoute } from "@tanstack/react-router";
import {
  AlertTriangleIcon,
  ArrowLeftIcon,
  BanIcon,
  KeyRoundIcon,
  LockIcon,
  RotateCwIcon,
} from "lucide-react";
import { toast } from "sonner";

import { ConfirmDialog } from "~/components/confirm-dialog";
import { CopyButton } from "~/components/copy-button";
import { DetailField, FieldGrid } from "~/components/detail-field";
import { JsonViewer } from "~/components/json-viewer";
import { PageHeader } from "~/components/page-header";
import { Pill, StatusBadge } from "~/components/status-badge";
import { Button } from "~/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "~/components/ui/card";
import { Skeleton } from "~/components/ui/skeleton";
import { errorMessage, isNotFound } from "~/lib/api";
import { EM_DASH, formatDateTime, formatDuration, formatRelative, orDash } from "~/lib/format";
import {
  useCallbackDelivery,
  useReplayCallbackDelivery,
  type CallbackDeliveryAttemptView,
  type CallbackDeliveryDetail,
} from "~/lib/queries";
import {
  HOUSEKEEPING,
  NextAttemptCountdown,
  Note,
  RETRY_SCHEDULE,
  isStaleLease,
} from "./-mechanics";

export const Route = createFileRoute("/_authed/callbacks/$deliveryId")({
  component: CallbackDeliveryPage,
});

/**
 * One outbound notification, and every attempt to deliver it.
 *
 * Three things this page explains rather than merely displays, because each of
 * them looks like a bug until you know the rule: the frozen snapshot, what
 * `exhausted` is and is not, and the retry schedule.
 */
function CallbackDeliveryPage() {
  const { deliveryId } = Route.useParams();
  const { data, error, isPending, refetch } = useCallbackDelivery(deliveryId);

  if (isPending) return <DetailSkeleton />;

  if (isNotFound(error)) {
    return (
      <EmptyState
        title="No such delivery"
        body="Nothing in callback_deliveries has this id."
      />
    );
  }

  if (error || !data) {
    return (
      <EmptyState
        title="Couldn't load this delivery"
        body={errorMessage(error)}
        onRetry={() => void refetch()}
      />
    );
  }

  return (
    <div className="space-y-5">
      <div>
        <Button variant="ghost" size="sm" render={<Link to="/callbacks" />}>
          <ArrowLeftIcon className="size-3.5" />
          All callbacks
        </Button>
      </div>

      <PageHeader
        title={
          <span className="flex items-center gap-2">
            <span className="font-mono text-xl">{data.order_id}</span>
            <CopyButton value={data.order_id} label="order id" />
          </span>
        }
        description={
          <span className="flex flex-wrap items-center gap-2">
            <StatusBadge status={data.status} />
            <span>{data.event_type}</span>
            <span className="text-muted-foreground">to {data.storefront_name}</span>
          </span>
        }
        actions={data.replayable ? <ReplayAction detail={data} /> : null}
      />

      {data.status === "exhausted" ? <Exhausted detail={data} /> : null}
      {data.status === "cancelled" ? <Cancelled detail={data} /> : null}
      {isStaleLease(data.locked_at) ? <StaleLease detail={data} /> : null}

      <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_22rem]">
        <div className="min-w-0 space-y-5">
          <Attempts detail={data} />
          <Snapshot detail={data} />
        </div>

        <div className="space-y-5">
          <Schedule detail={data} />
        </div>
      </div>
    </div>
  );
}

/**
 * Exhausted is not the DLQ, and conflating the two sends people looking in the
 * wrong place. Said here, next to the state it describes.
 */
function Exhausted({ detail }: { detail: CallbackDeliveryDetail }) {
  return (
    <Card className="border-destructive/30 bg-destructive/5">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base text-destructive">
          <AlertTriangleIcon className="size-4" />
          The merchant was never told
        </CardTitle>
        <CardDescription>
          All {detail.max_attempts} attempts were used without a 2xx. Nothing will retry this on
          its own.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {detail.last_error ? (
          <div className="flex items-start gap-1">
            <pre className="min-w-0 flex-1 overflow-x-auto rounded-md border border-destructive/20 bg-background p-3 font-mono text-xs whitespace-pre-wrap">
              {detail.last_error}
            </pre>
            <CopyButton value={detail.last_error} label="last error" />
          </div>
        ) : null}

        <Note>
          <strong className="font-medium text-foreground">
            Exhausted is not the dead-letter queue.
          </strong>{" "}
          Ordinary retry exhaustion sets this status; the SQS DLQ only ever holds poison
          messages that could not be processed at all. Anything sitting in the DLQ is a
          different, worse problem than this row.
        </Note>
      </CardContent>
    </Card>
  );
}

/**
 * The three different things `cancelled` means, and which one this is.
 *
 * The status covers a 410 Gone from the merchant, the callback URL being
 * removed, and the target resolving to a non-public address. They call for three
 * different responses and only last_error tells them apart, so it is shown
 * rather than summarised. The third is the SSRF guard in pkg/callbackurl, and a
 * replay into it fails in exactly the same way — worth knowing before pressing
 * the button rather than after.
 */
function Cancelled({ detail }: { detail: CallbackDeliveryDetail }) {
  return (
    <Card className="border-warning/30 bg-warning/5">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base text-warning">
          <BanIcon className="size-4" />
          Abandoned before its attempts ran out
        </CardTitle>
        <CardDescription>
          Cancelled after {detail.attempt_count} of {detail.max_attempts} attempts. Nothing will
          retry this on its own.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {detail.last_error ? (
          <div className="flex items-start gap-1">
            <pre className="min-w-0 flex-1 overflow-x-auto rounded-md border bg-background p-3 font-mono text-xs whitespace-pre-wrap">
              {detail.last_error}
            </pre>
            <CopyButton value={detail.last_error} label="cancellation reason" />
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">No reason was recorded.</p>
        )}

        <Note>
          <strong className="font-medium text-foreground">
            Cancelled covers three different problems.
          </strong>{" "}
          The merchant returned 410 Gone; their callback URL was removed; or the target
          resolved to a non-public address and the SSRF guard refused to dial it. The reason
          above says which. Replaying into the third will fail identically — that guard runs
          immediately before every connect, not at configuration time.
        </Note>
      </CardContent>
    </Card>
  );
}

/**
 * The replay button, and the one warning that has to come before it.
 *
 * Only rendered for exhausted and cancelled: pending and delivering are already
 * in flight, and a replay there is just a way to send the merchant the same
 * notification twice.
 *
 * The dialog spells out what a replay does and does not do, because the two are
 * easy to confuse. It re-queues the existing row — same frozen URL, same frozen
 * bytes — and lets the ordinary worker deliver it. It does not send to the
 * storefront's current URL; if that is what is wanted, that is a different
 * operation with different semantics.
 */
function ReplayAction({ detail }: { detail: CallbackDeliveryDetail }) {
  const [open, setOpen] = useState(false);
  const replay = useReplayCallbackDelivery(detail.id);

  const rotated = rotatedSinceEnqueue(detail);
  const moved =
    detail.storefront_callback_url != null &&
    detail.storefront_callback_url !== detail.target_url;
  const unhooked = !detail.storefront_callback_url || !detail.storefront_callback_enabled;

  return (
    <>
      <Button size="sm" onClick={() => setOpen(true)} disabled={replay.isPending}>
        <RotateCwIcon className="size-3.5" />
        Replay
      </Button>

      {replay.error ? (
        <span className="text-xs text-destructive">{errorMessage(replay.error)}</span>
      ) : null}

      <ConfirmDialog
        open={open}
        onOpenChange={setOpen}
        title="Replay this delivery?"
        description={`${detail.event_type} for ${detail.order_id}, to ${detail.storefront_name}.`}
        confirmLabel="Replay"
        pending={replay.isPending}
        onConfirm={() => {
          replay.mutate(undefined, {
            onSuccess: (result) => {
              setOpen(false);
              toast.success(
                result.signalled
                  ? "Re-queued. The worker will attempt it shortly."
                  : "Re-queued. No delivery queue is configured, so the worker will pick it up on its next hourly sweep.",
              );
            },
          });
        }}
        body={
          <>
            <p className="text-muted-foreground">
              The delivery goes back in the queue with a fresh set of attempts. Attempts
              already made are kept, so the history below stays intact and the new attempt
              continues its numbering.
            </p>

            <Note>
              <strong className="font-medium text-foreground">
                It will be sent to the frozen target, not the current one.
              </strong>{" "}
              The URL and the payload were captured at enqueue so a configuration change
              cannot redirect a retry and every attempt sends byte-identical bytes.
              Delivering to a different URL is a different operation, not a replay.
            </Note>

            {moved ? (
              <p className="rounded-md border border-warning/30 bg-warning/10 p-2.5 text-xs">
                This storefront's callback URL has changed since this delivery was enqueued.
                The replay goes to{" "}
                <code className="font-mono break-all">{detail.target_url}</code>, not to{" "}
                <code className="font-mono break-all">{detail.storefront_callback_url}</code>.
              </p>
            ) : null}

            {unhooked ? (
              <p className="rounded-md border border-warning/30 bg-warning/10 p-2.5 text-xs">
                This storefront has no callback configured, or has callbacks switched off. The
                worker resolves the target immediately before each attempt, so the replay will
                be cancelled again rather than delivered.
              </p>
            ) : null}

            {rotated ? (
              <p className="flex items-start gap-2 rounded-md border border-warning/30 bg-warning/10 p-2.5 text-xs">
                <KeyRoundIcon className="mt-0.5 size-3.5 shrink-0 text-warning" />
                <span>
                  The signing secret was rotated{" "}
                  {formatRelative(detail.storefront_callback_secret_rotated_at)}, after this
                  delivery was enqueued. Each attempt is signed fresh with the current secret,
                  so a merchant still verifying against the old one will reject this.
                </span>
              </p>
            ) : null}
          </>
        }
      />
    </>
  );
}

/**
 * Whether the signing secret changed after this delivery was created.
 *
 * Worth its own function because the consequence is not obvious: signatures are
 * per-attempt (`v1:<timestamp>:<body>` over the current secret), so a rotation
 * that happened long after an enqueue still reaches that delivery's next
 * attempt. A merchant pinned to the pre-rotation secret rejects it, and the
 * replay looks like it failed for no reason.
 */
function rotatedSinceEnqueue(detail: CallbackDeliveryDetail): boolean {
  if (!detail.storefront_callback_secret_rotated_at) return false;
  const rotated = new Date(detail.storefront_callback_secret_rotated_at).getTime();
  const enqueued = new Date(detail.created_at).getTime();
  if (Number.isNaN(rotated) || Number.isNaN(enqueued)) return false;
  return rotated > enqueued;
}

function StaleLease({ detail }: { detail: CallbackDeliveryDetail }) {
  return (
    <Card className="border-warning/30 bg-warning/5">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base text-warning">
          <LockIcon className="size-4" />
          Lease held longer than five minutes
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-muted-foreground">
          <code className="font-mono text-xs">{orDash(detail.locked_by)}</code> claimed this
          delivery {formatRelative(detail.locked_at)} and never released it, which means that
          worker died mid-flight. Housekeeping runs {HOUSEKEEPING} and will return the row to
          the queue — deliberately slower than the lease, because it runs longer than Neon's
          autosuspend window. Nothing needs doing unless it is still here after the next sweep.
        </p>
      </CardContent>
    </Card>
  );
}

/** The attempt history as a chronology: what we sent, and what came back. */
function Attempts({ detail }: { detail: CallbackDeliveryDetail }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">
          Attempts{" "}
          <span className="font-normal text-muted-foreground tabular-nums">
            {detail.attempt_count} / {detail.max_attempts}
          </span>
        </CardTitle>
        <CardDescription>
          Every HTTP call made for this delivery, oldest first.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {detail.attempts.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No attempt has been recorded yet.
            {detail.attempt_count > 0
              ? " attempt_count is incremented when a worker claims the row, before the call is made, so a count without a matching row means a worker died between the two."
              : ""}
          </p>
        ) : (
          <ol className="space-y-3">
            {detail.attempts.map((attempt) => (
              <Attempt
                key={attempt.attempt_number}
                attempt={attempt}
                total={detail.max_attempts}
              />
            ))}
          </ol>
        )}
      </CardContent>
    </Card>
  );
}

function Attempt({
  attempt,
  total,
}: {
  attempt: CallbackDeliveryAttemptView;
  total: number;
}) {
  // A transport failure never got a response, so it has an error and no status
  // code. Both shapes have to render.
  const ok = attempt.status_code != null && attempt.status_code >= 200 && attempt.status_code < 300;

  return (
    <li className="rounded-lg border p-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-medium tabular-nums">
          Attempt {attempt.attempt_number} of {total}
        </span>

        {attempt.status_code != null ? (
          <Pill tone={ok ? "success" : "danger"}>HTTP {attempt.status_code}</Pill>
        ) : (
          <Pill tone="danger">no response</Pill>
        )}

        <span className="text-xs text-muted-foreground tabular-nums">
          {formatDuration(attempt.duration_ms)}
        </span>

        <span
          className="ml-auto text-xs text-muted-foreground"
          title={formatDateTime(attempt.started_at)}
        >
          {formatRelative(attempt.started_at)}
        </span>
      </div>

      {attempt.error ? (
        <pre className="mt-2 overflow-x-auto rounded-md border border-destructive/20 bg-destructive/5 p-2 font-mono text-xs whitespace-pre-wrap">
          {attempt.error}
        </pre>
      ) : null}

      {attempt.response_body ? (
        <pre className="mt-2 max-h-40 overflow-auto rounded-md border bg-muted/40 p-2 font-mono text-xs">
          {attempt.response_body}
        </pre>
      ) : null}

      {attempt.worker_id ? (
        <p className="mt-2 font-mono text-[0.6875rem] text-muted-foreground">
          {attempt.worker_id}
        </p>
      ) : null}
    </li>
  );
}

/**
 * The frozen snapshot, and the explanation that stops it reading as a bug.
 */
function Snapshot({ detail }: { detail: CallbackDeliveryDetail }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">What we sent</CardTitle>
        <CardDescription>
          Captured when the delivery was enqueued, not when it was attempted.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <FieldGrid className="sm:grid-cols-1 lg:grid-cols-1">
          <DetailField label="Target URL" copy={detail.target_url} mono>
            {detail.target_url}
          </DetailField>
        </FieldGrid>

        <Note>
          <strong className="font-medium text-foreground">
            The URL and the payload are frozen at enqueue.
          </strong>{" "}
          Changing a merchant's callback URL cannot redirect a delivery already in flight, and
          every attempt sends byte-identical bytes. A target that no longer matches the
          storefront's current configuration is this rule working, not a bug.
        </Note>

        <JsonViewer value={detail.payload} label="payload" searchable />
      </CardContent>
    </Card>
  );
}

function Schedule({ detail }: { detail: CallbackDeliveryDetail }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Schedule</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <FieldGrid className="sm:grid-cols-1 lg:grid-cols-1">
          <DetailField label="Enqueued">
            <span title={formatDateTime(detail.created_at)}>
              {formatRelative(detail.created_at)}
            </span>
          </DetailField>
          <DetailField label="Last attempt">
            {detail.last_attempt_at ? (
              <span title={formatDateTime(detail.last_attempt_at)}>
                {formatRelative(detail.last_attempt_at)}
              </span>
            ) : (
              EM_DASH
            )}
          </DetailField>
          <DetailField label="Next attempt">
            <NextAttemptCountdown at={detail.next_attempt_at} />
          </DetailField>
          <DetailField label="Delivered">{formatDateTime(detail.delivered_at)}</DetailField>
          <DetailField label="Last status code">
            {detail.last_status_code ?? EM_DASH}
          </DetailField>
        </FieldGrid>

        <Note>
          Retries back off {RETRY_SCHEDULE}, each with ±20% jitter and clamping at the last —
          {" "}
          {detail.max_attempts} attempts over roughly 8.7 hours. A delivery that has not been
          retried is almost always one that is not due yet.
        </Note>
      </CardContent>
    </Card>
  );
}

function DetailSkeleton() {
  return (
    <div className="space-y-5">
      <Skeleton className="h-8 w-64" />
      <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_22rem]">
        <div className="space-y-5">
          <Skeleton className="h-64 w-full" />
          <Skeleton className="h-48 w-full" />
        </div>
        <Skeleton className="h-64 w-full" />
      </div>
    </div>
  );
}

function EmptyState({
  title,
  body,
  onRetry,
}: {
  title: string;
  body: React.ReactNode;
  onRetry?: () => void;
}) {
  return (
    <div className="mx-auto max-w-lg">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">{title}</CardTitle>
          <CardDescription>{body}</CardDescription>
        </CardHeader>
        <CardContent className="flex gap-2">
          {onRetry ? (
            <Button variant="outline" size="sm" onClick={onRetry}>
              Try again
            </Button>
          ) : null}
          <Button variant="ghost" size="sm" render={<Link to="/callbacks" />}>
            Back to callbacks
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
